import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ChevronDown, Inbox, UserPlus } from 'lucide-react';
import { listPeople } from '@/api/people';
import { listAccountStaging } from '@/api/accountStaging';
import { getLocations } from '@/api/locations';
import { useAuth } from '@/auth/useAuth';
import { accountStatuses } from './schema';
import { formatDateTime } from '@/lib/dateFormat';
import type { AccountStatus } from '@/types/oscar';
import { useSort } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import { DesktopTable, MobileCards, MobileCard, CardField } from '@/components/common/responsiveList';

const PAGE_SIZE = 50;

/** Default status filter: everything except Terminated. */
const DEFAULT_STATUSES: AccountStatus[] = accountStatuses.filter((s) => s !== 'Terminated');

const statusBadge: Record<AccountStatus, string> = {
  Active: 'bg-green/15 text-green',
  Onboarding: 'bg-lightBlue/20 text-navy',
  Offboarding: 'bg-orange/15 text-orange',
  OnLeave: 'bg-darkGrey/10 text-darkGrey',
  Terminated: 'bg-red/15 text-red',
};

/** Debounce a value by the given delay (ms). */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function PeopleRegister() {
  const { canMutate } = useAuth();
  const [statuses, setStatuses] = useState<AccountStatus[]>(DEFAULT_STATUSES);
  const [location, setLocation] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const search = useDebounced(searchInput);
  const { sortKey, sortDir, onSort } = useSort('name');
  const handleSort = (k: string) => { onSort(k); setPage(1); };

  const locationsQuery = useQuery({ queryKey: ['locations', 'active'], queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal), retry: false });
  const locationName = new Map((locationsQuery.data ?? []).map((l) => [l.LocationId, l.LocationDesc] as const));

  // Count of accounts awaiting a categorised add (only for those who can add).
  const pendingQuery = useQuery({
    queryKey: ['account-staging', 'pending-count'],
    queryFn: ({ signal }) => listAccountStaging(undefined, signal),
    enabled: canMutate,
    retry: false,
  });
  const pendingCount = (pendingQuery.data ?? []).filter((a) => a.TriageStatus === 'New' || a.TriageStatus === 'NeedsVerify').length;

  // All selected (or none) = no status filter; otherwise a comma-separated list.
  const status = statuses.length === 0 || statuses.length === accountStatuses.length ? '' : statuses.join(',');

  const query = useQuery({
    queryKey: ['people', { status, location, search, sortKey, sortDir, page }],
    queryFn: ({ signal }) =>
      listPeople({ status, location: location ? Number(location) : undefined, search, sort: sortKey, dir: sortDir, page, pageSize: PAGE_SIZE }, signal),
    placeholderData: keepPreviousData,
  });

  const total = query.data?.Total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">People / Accounts</h1>
          <p className="mt-1 text-sm text-darkGrey/80">Staff, contractors, and all Entra accounts — categorised by Account Type.</p>
        </div>
        {canMutate && (
          <div className="flex items-center gap-2">
            <Link
              to="/people/pending"
              className="inline-flex items-center gap-2 rounded border border-navy/30 bg-white px-3 py-2 text-sm font-semibold text-navy hover:bg-bg"
            >
              <Inbox className="h-4 w-4" aria-hidden /> Pending accounts
              {pendingCount > 0 && (
                <span aria-label={`${pendingCount} pending`} className="ml-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-orange px-1.5 py-0.5 text-xs font-bold leading-none text-white">
                  {pendingCount}
                </span>
              )}
            </Link>
            <Link
              to="/people/new"
              className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              <UserPlus className="h-4 w-4" aria-hidden /> Add person
            </Link>
          </div>
        )}
      </header>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="flex flex-col text-xs font-semibold text-darkGrey">
          Status
          <StatusFilter value={statuses} onChange={(next) => { setStatuses(next); setPage(1); }} />
        </div>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Location
          <select
            aria-label="Filter by location"
            value={location}
            onChange={(e) => { setLocation(e.target.value); setPage(1); }}
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          >
            <option value="">All</option>
            {(locationsQuery.data ?? []).map((l) => <option key={l.LocationId} value={l.LocationId}>{l.LocationDesc}</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input
            aria-label="Search people"
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            placeholder="Name, email or employee ID"
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          />
        </label>
      </div>

      <DesktopTable className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <SortHeader label="Name" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Job title" columnKey="jobTitle" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2">Location</th>
              <SortHeader label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Email" columnKey="email" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Last sign-in" columnKey="lastSignIn" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>
            )}
            {query.isError && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-red">Could not load people.</td></tr>
            )}
            {query.data?.Items.length === 0 && !query.isLoading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-darkGrey/60">No people match the filters.</td></tr>
            )}
            {query.data?.Items.map((p) => (
              <tr key={p.PersonId} className="border-t border-border hover:bg-bg/60">
                <td className="px-3 py-2">
                  <Link to={`/people/${p.PersonId}`} className="font-medium text-navy hover:underline">
                    {p.DisplayName}
                  </Link>
                </td>
                <td className="px-3 py-2 text-darkGrey/80">{p.JobTitle ?? '—'}</td>
                <td className="px-3 py-2 text-darkGrey/80">{p.LocationId != null ? (locationName.get(p.LocationId) ?? `#${p.LocationId}`) : '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge[p.AccountStatus]}`}>
                    {p.AccountStatus}
                  </span>
                  {p.Active === false && (
                    <span
                      title="Disabled in Entra (synced)"
                      className="ml-1 inline-flex rounded-full bg-red/10 px-2 py-0.5 text-xs font-semibold text-red"
                    >
                      Disabled
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-darkGrey/80">{p.PrimaryEmail}</td>
                <td className="px-3 py-2 text-darkGrey/80">{p.LastSignInDateTime ? formatDateTime(p.LastSignInDateTime) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>

      {/* Mobile (<md): people as stacked cards. */}
      <MobileCards>
        {query.isLoading && <p className="px-1 py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
        {query.isError && <p className="px-1 py-6 text-center text-sm text-red">Could not load people.</p>}
        {query.data?.Items.length === 0 && !query.isLoading && (
          <p className="px-1 py-6 text-center text-sm text-darkGrey/60">No people match the filters.</p>
        )}
        {query.data?.Items.map((p) => (
          <MobileCard key={p.PersonId}>
            <div className="flex items-start justify-between gap-2">
              <Link to={`/people/${p.PersonId}`} className="text-sm font-semibold text-navy hover:text-orange hover:underline">
                {p.DisplayName}
              </Link>
              <span className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge[p.AccountStatus]}`}>
                  {p.AccountStatus}
                </span>
                {p.Active === false && (
                  <span title="Disabled in Entra (synced)" className="inline-flex rounded-full bg-red/10 px-2 py-0.5 text-xs font-semibold text-red">
                    Disabled
                  </span>
                )}
              </span>
            </div>
            <div className="mt-2 border-t border-border pt-2">
              <CardField label="Job title">{p.JobTitle ?? '—'}</CardField>
              <CardField label="Location">{p.LocationId != null ? (locationName.get(p.LocationId) ?? `#${p.LocationId}`) : '—'}</CardField>
              <CardField label="Email"><span className="break-all">{p.PrimaryEmail}</span></CardField>
              <CardField label="Last sign-in">{p.LastSignInDateTime ? formatDateTime(p.LastSignInDateTime) : '—'}</CardField>
            </div>
          </MobileCard>
        ))}
      </MobileCards>

      <div className="mt-3 flex items-center justify-between text-sm text-darkGrey/80">
        <span>{total} {total === 1 ? 'person' : 'people'}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-border bg-white px-2 py-1 disabled:opacity-40"
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded border border-border bg-white px-2 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * Multi-select status filter — a button that opens a checkbox panel. Ticking
 * none or all is treated the same as "All" by the caller. Closes on outside
 * click or Escape. Mirrors the plain-select styling of the other filters.
 */
function StatusFilter({ value, onChange }: { value: AccountStatus[]; onChange: (next: AccountStatus[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const toggle = (s: AccountStatus) =>
    onChange(value.includes(s) ? value.filter((v) => v !== s) : [...value, s]);

  const summary =
    value.length === 0 || value.length === accountStatuses.length
      ? 'All'
      : value.length === 1
        ? value[0]
        : `${value.length} selected`;

  return (
    <div ref={ref} className="relative mt-1">
      <button
        type="button"
        aria-label="Filter by status"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-52 items-center justify-between gap-2 rounded border border-border bg-white px-2 py-1.5 text-left text-sm font-normal"
      >
        <span className={value.length === 0 || value.length === accountStatuses.length ? 'text-darkGrey/60' : 'text-darkGrey'}>{summary}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-darkGrey/50" aria-hidden />
      </button>
      {open && (
        <div role="listbox" aria-label="Status options" className="absolute z-10 mt-1 w-52 rounded border border-border bg-white p-1 text-sm font-normal shadow-lg">
          <div className="flex items-center justify-between px-2 py-1 text-xs">
            <button type="button" onClick={() => onChange([...accountStatuses])} className="font-semibold text-navy hover:underline">All</button>
            <button type="button" onClick={() => onChange([])} className="font-semibold text-navy hover:underline">Clear</button>
          </div>
          {accountStatuses.map((s) => (
            <label key={s} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-bg">
              <input type="checkbox" checked={value.includes(s)} onChange={() => toggle(s)} />
              <span className="text-darkGrey">{s}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
